import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatError } from "@/api";
import { useAuth } from "@/auth";
import { Card, Btn, Spinner } from "@/components/ui-kit";
import { ProBadge } from "@/components/dashboard-bits";
import { Check } from "lucide-react";

const BENEFITS = {
  creator_pro: [
    "Rajattomat hakemukset",
    "Näy sisällöntuottajahakemistossa",
    "Yritykset voivat ottaa sinuun suoraan yhteyttä",
    "Pro-merkki",
  ],
  business_pro: [
    "Rajattomat aktiiviset kampanjat",
    "Pääsy sisällöntuottajahakemistoon",
    "Suora yhteydenotto Creatori Pro -jäseniin",
  ],
};

export default function Subscription() {
  const { checkAuth } = useAuth();
  const [params, setParams] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const polled = useRef(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/subscriptions/me");
    setInfo(data);
  }, []);

  const pollStatus = useCallback(async (sessionId, attempt = 0) => {
    if (attempt >= 6) { setVerifying(false); toast.error("Maksun vahvistus kesti liian kauan. Päivitä sivu hetken kuluttua."); return; }
    try {
      const { data } = await api.get(`/subscriptions/status/${sessionId}`);
      if (data.payment_status === "paid") {
        toast.success("Tilaus aktivoitiin onnistuneesti.");
        await checkAuth();
        await load();
        setVerifying(false);
        return;
      }
      if (data.status === "expired") { setVerifying(false); toast.error("Maksu vanheni. Yritä uudelleen."); return; }
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
    } catch {
      setVerifying(false);
      toast.error("Maksun tilan tarkistus epäonnistui.");
    }
  }, [checkAuth, load]);

  useEffect(() => {
    load();
    if (polled.current) return;
    polled.current = true;
    const sessionId = params.get("session_id");
    const canceled = params.get("canceled");
    if (sessionId) {
      setVerifying(true);
      pollStatus(sessionId);
      setParams({}, { replace: true });
    } else if (canceled) {
      toast.info("Maksu keskeytettiin.");
      setParams({}, { replace: true });
    }
  }, [load, params, setParams, pollStatus]);

  const managePortal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/subscriptions/portal", { origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.info(formatError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!info) return <Spinner />;

  const benefits = BENEFITS[info.plan_key] || [];
  const isPro = info.is_pro;

  return (
    <div className="max-w-xl" data-testid="subscription-page">
      <h3 className="text-2xl font-semibold tracking-display text-[#292827] mb-6">Jäsenyys</h3>

      <Card className="p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h4 className="text-xl font-semibold tracking-tightish text-[#292827]">{info.plan_name}</h4>
              {isPro && <ProBadge />}
            </div>
          </div>
          {isPro && (
            <span data-testid="subscription-active-badge" className="rounded-full bg-[#E9EAEC] px-3 py-1 text-[13px] font-medium text-[#292827]">Aktiivinen</span>
          )}
        </div>

        <div className="mt-7 space-y-3">
          {benefits.map((b) => (
            <div key={b} className="flex items-center gap-3 text-sm text-[#292827]">
              <span className="h-6 w-6 rounded-full bg-[#E9EAEC] flex items-center justify-center shrink-0"><Check size={14} strokeWidth={2} /></span>
              {b}
            </div>
          ))}
        </div>

        <div className="mt-8">
          {isPro ? (
            <Btn variant="outline" className="w-full" onClick={managePortal} disabled={busy} data-testid="manage-subscription">
              Hallitse tilausta
            </Btn>
          ) : (
            <>
              <Btn className="w-full" disabled data-testid="upgrade-pro">
                Tulossa pian
              </Btn>
              <p className="mt-3 text-center text-xs text-[#968F85]">Pro-jäsenyys julkaistaan myöhemmin.</p>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
